import React, { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  Save,
  X,
  Building2,
  Users,
  Briefcase,
  Mail,
  Globe,
  MapPin
} from 'lucide-react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import toast from 'react-hot-toast'

// Validation schema
const entitySchema = z.object({
  name: z.string()
    .min(1, 'Name is required')
    .max(255, 'Name must be less than 255 characters'),
  address: z.string()
    .max(500, 'Address must be less than 500 characters')
    .optional(),
  email: z.string()
    .email('Please enter a valid email address')
    .optional()
    .or(z.literal('')),
  website: z.string()
    .url('Please enter a valid URL')
    .optional()
    .or(z.literal('')),
})

type EntityFormData = z.infer<typeof entitySchema>

interface SavedEntity {
  id?: number
  name: string
}

interface EntityEditFormProps {
  type: 'architect' | 'developer' | 'broker'
  entityId?: number | null
  onClose: () => void
  onSuccess: (entity: SavedEntity) => void
}

const entityLabels = {
  architect: {
    title: 'Architect',
    icon: Building2,
    endpoint: 'architects'
  },
  developer: {
    title: 'Developer',
    icon: Users,
    endpoint: 'developers'
  },
  broker: {
    title: 'Broker',
    icon: Briefcase,
    endpoint: 'brokers'
  }
}

export function EntityEditForm({ type, entityId, onClose, onSuccess }: EntityEditFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const queryClient = useQueryClient()
  const entityConfig = entityLabels[type]
  const Icon = entityConfig.icon

  const isEditing = !!entityId

  // Fetch existing entity data if editing
  const { data: existingEntity, isLoading } = useQuery({
    queryKey: [`${type}`, entityId],
    queryFn: async () => {
      if (!entityId) return null
      const response = await api.get(`/${entityConfig.endpoint}/${entityId}`)
      return response.data
    },
    enabled: isEditing,
  })

  const form = useForm<EntityFormData>({
    resolver: zodResolver(entitySchema),
    defaultValues: {
      name: '',
      address: '',
      email: '',
      website: '',
    },
  })

  // Populate form with existing data when editing
  useEffect(() => {
    if (existingEntity) {
      form.reset({
        name: existingEntity.name || '',
        address: existingEntity.address || '',
        email: existingEntity.email || '',
        website: existingEntity.website || '',
      })
    }
  }, [existingEntity, form])

  // Create or update mutation
  const mutation = useMutation({
    mutationFn: async (data: EntityFormData) => {
      const cleanData = {
        ...data,
        email: data.email || undefined,
        website: data.website || undefined,
        address: data.address || undefined,
      }

      if (isEditing && entityId) {
        return api.put(`/${entityConfig.endpoint}/${entityId}`, cleanData)
      } else {
        return api.post(`/${entityConfig.endpoint}`, cleanData)
      }
    },
    onSuccess: (response, variables) => {
      queryClient.invalidateQueries({ queryKey: [entityConfig.endpoint] })
      queryClient.invalidateQueries({ queryKey: [`/${entityConfig.endpoint}-dropdown`] })
      queryClient.invalidateQueries({ queryKey: [`${type}`, entityId] })
      toast.success(`${entityConfig.title} ${isEditing ? 'updated' : 'created'} successfully`)

      const body = response?.data ?? {}
      const resolvedId =
        body.id ??
        body[`${type}_id`] ??
        entityId ??
        null
      const resolvedName = body.name ?? variables.name
      onSuccess({
        id: resolvedId != null ? Number(resolvedId) : undefined,
        name: resolvedName,
      })
    },
    onError: (error: any) => {
      console.error(`Error ${isEditing ? 'updating' : 'creating'} ${type}:`, error)
      const errorMessage = error?.response?.data?.message
      const displayMessage = Array.isArray(errorMessage)
        ? errorMessage[0]
        : errorMessage || `Failed to ${isEditing ? 'update' : 'create'} ${type}`
      toast.error(displayMessage)
    },
    onSettled: () => {
      setIsSubmitting(false)
    },
  })

  const onSubmit = async (data: EntityFormData) => {
    setIsSubmitting(true)
    mutation.mutate(data)
  }

  if (isEditing && isLoading) {
    return createPortal(
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl p-6">
          <div className="flex items-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600 mr-3"></div>
            Loading...
          </div>
        </div>
      </div>,
      document.body
    )
  }

  return createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-tagus max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="border-b border-secondary-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Icon className="h-6 w-6 text-primary-600 mr-3" />
              <h2 className="text-xl font-semibold text-secondary-900">
                {isEditing ? `Edit ${entityConfig.title}` : `Add ${entityConfig.title}`}
              </h2>
            </div>
            <button
              onClick={onClose}
              className="text-secondary-400 hover:text-secondary-600"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Form */}
        <form
          onSubmit={(e) => {
            // Prevent the submit event from bubbling up to any enclosing
            // <form> (e.g. when this modal is rendered inside another form
            // such as CreateProject's main form).
            e.stopPropagation()
            return form.handleSubmit(onSubmit)(e)
          }}
          className="p-6 space-y-6"
        >
          {/* Name */}
          <div>
            <label htmlFor="name" className="form-label">
              Name *
            </label>
            <input
              id="name"
              type="text"
              {...form.register('name')}
              className="form-input"
              placeholder={`Enter ${type} name`}
            />
            {form.formState.errors.name && (
              <p className="mt-1 text-sm text-danger-600">{form.formState.errors.name.message}</p>
            )}
          </div>

          {/* Address */}
          <div>
            <label htmlFor="address" className="form-label">
              <MapPin className="h-4 w-4 inline mr-1" />
              Address
            </label>
            <input
              id="address"
              type="text"
              {...form.register('address')}
              className="form-input"
              placeholder="Enter address"
            />
            {form.formState.errors.address && (
              <p className="mt-1 text-sm text-danger-600">{form.formState.errors.address.message}</p>
            )}
          </div>

          {/* Email */}
          <div>
            <label htmlFor="email" className="form-label">
              <Mail className="h-4 w-4 inline mr-1" />
              Email
            </label>
            <input
              id="email"
              type="email"
              {...form.register('email')}
              className="form-input"
              placeholder="Enter email address"
            />
            {form.formState.errors.email && (
              <p className="mt-1 text-sm text-danger-600">{form.formState.errors.email.message}</p>
            )}
          </div>

          {/* Website */}
          <div>
            <label htmlFor="website" className="form-label">
              <Globe className="h-4 w-4 inline mr-1" />
              Website
            </label>
            <input
              id="website"
              type="url"
              {...form.register('website')}
              className="form-input"
              placeholder="https://example.com"
            />
            {form.formState.errors.website && (
              <p className="mt-1 text-sm text-danger-600">{form.formState.errors.website.message}</p>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end space-x-3 pt-4 border-t border-secondary-200">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="btn-primary"
            >
              {isSubmitting ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  {isEditing ? 'Updating...' : 'Creating...'}
                </div>
              ) : (
                <div className="flex items-center">
                  <Save className="h-4 w-4 mr-2" />
                  {isEditing ? 'Update' : 'Create'} {entityConfig.title}
                </div>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  )
}

